import {
	AuthRoleEnum,
	StatusEnum,
	useDeleteAdminUserMutation,
	useDeleteManyAdminUserMutation,
	useGetAdminUserStatsQuery,
	useGetAllAdminUserQuery,
} from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { toast } from 'sonner';
import { MoreHorizontal, Pencil, Trash2, UserPlus, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useListParams } from '../../hooks/useListParams';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { SortableTableHead } from '../../components/ui/sortable-table-head';
import { Tabs } from '../../components/ui/tabs';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { SelectionBar } from '../../components/ui/selection-bar';
import { StatusBadge } from '../../components/ui/status-badge';
import { SearchInput } from '../../components/ui/search-input';
import { InitialsAvatar } from '../../components/ui/initials-avatar';
import { ViewToggle } from '../../components/ui/view-toggle';
import { EntityCard } from '../../components/ui/entity-card';
import { formatDate } from '../../lib/utils';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useAuth } from '../../hooks/useAuth';
import { CardSkeletons, TableSkeleton } from '../../components/ui/list-skeletons';
import { EmptyState } from '../../components/ui/empty-state';
import { InviteUserModal } from './InviteUserModal';
import { EditUserModal } from './EditUserModal';

/* Maps the UI tab to the backend status filter (COMPLETED = active, PENDING = invited). */
function tabToStatus(tab: string): StatusEnum | undefined {
	if (tab === 'active') return StatusEnum.Completed;
	if (tab === 'pending') return StatusEnum.Pending;
	return undefined;
}

function userStatusLabel(lockedAt?: string | null, lastActive?: string | null) {
	if (lockedAt) return 'Locked';
	return lastActive ? 'Active' : 'Pending';
}

/* Status badge: Locked takes precedence, else Active / Pending. */
function UserStatusBadge({
	lockedAt,
	lastActive,
}: {
	lockedAt?: string | null;
	lastActive?: string | null;
}) {
	if (lockedAt) {
		return <StatusBadge active activeLabel="Locked" activeTone="danger" />;
	}
	return <StatusBadge active={!!lastActive} inactiveLabel="Pending" inactiveTone="warn" />;
}

/**
 * One row of users as laid out at the current viewport, for sizing scroll-mode batches.
 * Mirrors the card grid below (1/2/3 columns at sm/lg, 14px gap) of EntityCards with three
 * metrics: 16px padding, 44px avatar header, 14px spacing and a metrics strip a little taller
 * than text for the role badge. A table row is the 32px avatar plus cell padding and border.
 */
function userRows(view: string): RowEstimate {
	if (view !== 'card') return { rowHeight: 53 };
	const vw = window.innerWidth;
	const columns = vw >= 1024 ? 3 : vw >= 640 ? 2 : 1;
	return { columns, rowHeight: 145 + 14 };
}

/* ════════════════════════ USERS PAGE ════════════════════════ */

export function UsersPage() {
	useSetPageMeta('Users', [{ label: 'Users' }]);
	const { user: currentUser } = useAuth();
	const {
		search,
		setSearch,
		debouncedSearch,
		page,
		setPage,
		view,
		setView,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		limit,
		mode,
		setMode,
		request,
		loadMore,
		reload,
	} = useListParams({
		defaultSort: 'firstName',
		defaultDir: 'asc',
		defaultView: 'list',
		scrollRows: userRows,
	});
	const [tab, setTab] = useQueryParam('tab', 'all');
	const [inviteOpen, setInviteOpen] = useState(false);
	const [editTarget, setEditTarget] = useState<{
		id: string;
		firstName: string;
		lastName: string;
		email?: string;
	} | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

	const { data, loading, refetch } = useGetAllAdminUserQuery({
		variables: {
			input: {
				pagination: request,
				search: debouncedSearch || undefined,
				order: { field: sortField, direction: sortDirection },
				status: tabToStatus(tab),
			},
		},
		fetchPolicy: 'no-cache',
	});

	// KPI counts come from a dedicated stats endpoint (global, not page-scoped).
	const { data: statsData, refetch: refetchStats } = useGetAdminUserStatsQuery({
		fetchPolicy: 'no-cache',
	});
	const stats = statsData?.getAdminUserStats;

	// Rows are already filtered by the active status tab server-side.
	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getAllAdminUser,
		loading
	);
	const users = list.items;

	// The signed-in admin can't delete their own account. currentUser.id is the
	// auth-user id, which matches each row's authenticationUser.id.
	const isSelf = (u: (typeof users)[number]) =>
		!!currentUser && u.authenticationUser?.id === currentUser.id;

	const [deleteAdminUser, { loading: deleting }] = useDeleteAdminUserMutation();

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			const { data } = await deleteAdminUser({ variables: { id: deleteTarget.id } });
			if (data?.deleteAdminUser?.success) {
				toast.success('User deleted');
				setDeleteTarget(null);
				// If this was the last row on a non-first page, step back so we
				// don't strand the user on an empty page (setPage re-runs the query).
				// Scroll mode keeps every loaded row on screen, so it just reloads.
				if (mode === 'pages' && users.length <= 1 && page > 1) {
					setPage(page - 1);
				} else {
					reload(refetch);
				}
				refetchStats();
			} else {
				toast.error(data?.deleteAdminUser?.message || 'Failed to delete user');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete user');
		}
	};

	/* ── Selection state ── */
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

	// Selectable excludes the current user so they can't bulk-delete themselves.
	const selectableIds = useMemo(
		() => users.filter((u) => !isSelf(u)).map((u) => u.id),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[users, currentUser?.id]
	);
	const allSelected =
		selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
	const someSelected = selectableIds.some((id) => selectedIds.has(id));

	const toggleOne = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		if (allSelected) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(selectableIds));
		}
	};

	// Clear selection when the rows on screen are replaced. Scrolling keeps earlier rows on
	// screen, so in scroll mode loading more does not clear it.
	const shownPage = mode === 'pages' ? page : 0;
	useEffect(() => {
		setSelectedIds(new Set());
	}, [shownPage, mode, debouncedSearch, tab]);

	/* ── Bulk delete ── */
	const [deleteManyAdminUser, { loading: bulkDeleting }] = useDeleteManyAdminUserMutation();
	const handleBulkDelete = async () => {
		const count = selectedIds.size;
		try {
			const { data } = await deleteManyAdminUser({
				variables: { input: { ids: Array.from(selectedIds) } },
			});
			if (data?.deleteManyAdminUser?.success) {
				toast.success(`${count} user${count > 1 ? 's' : ''} deleted`);
				setSelectedIds(new Set());
				setBulkDeleteOpen(false);
				// Step back if we just emptied a non-first page (setPage re-runs the query).
				if (mode === 'pages' && users.length - count <= 0 && page > 1) {
					setPage(page - 1);
				} else {
					reload(refetch);
				}
				refetchStats();
			} else {
				toast.error(data?.deleteManyAdminUser?.message || 'Failed to delete users');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete users');
		}
	};

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Users
					</h1>
					<p className="text-base text-text-3">
						Manage admin user accounts and permissions.
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
						<UserPlus size={14} data-icon="inline-start" />
						Invite user
					</Button>
				</div>
			</div>

			{/* ── KPI strip ── */}
			<div className="mb-7 grid grid-cols-1 divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken sm:grid-cols-3 sm:divide-x sm:divide-y-0">
				{[
					{ label: 'Total users', value: stats?.total ?? '—', sub: 'All admin users' },
					{
						label: 'Active',
						value: stats?.active ?? '—',
						sub: 'Signed in',
					},
					{
						label: 'Pending',
						value: stats?.pending ?? '—',
						sub: 'Invited',
					},
				].map((k) => (
					<div key={k.label} className="px-5 py-4">
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{k.label}
						</div>
						<div className="mb-1 font-mono text-2xl font-medium text-text-1">
							{k.value}
						</div>
						<div className="text-xs text-text-3">{k.sub}</div>
					</div>
				))}
			</div>

			{/* ── Tabs + filters row ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All users' },
						{ value: 'active', label: 'Active' },
						{ value: 'pending', label: 'Pending' },
					]}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search users…"
						className="flex-1 lg:flex-none lg:w-[240px]"
					/>
					<ViewToggle value={view} onChange={setView} />
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{/* ── Content ── */}
			{list.loading ? (
				view === 'card' ? (
					<CardSkeletons />
				) : (
					<TableSkeleton />
				)
			) : users.length === 0 ? (
				<EmptyState
					icon={Users}
					title="No users found"
					description={
						search
							? 'Try a different search term.'
							: tab === 'active'
								? 'No active users yet.'
								: tab === 'pending'
									? 'No pending invitations.'
									: 'Invite your first admin user.'
					}
				/>
			) : view === 'card' ? (
				/* ── Card grid ── */
				<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
					{users.map((u) => (
						<EntityCard
							key={u.id}
							name={`${u.firstName} ${u.lastName}`}
							subtitle={u.authenticationUser?.email || 'No email'}
							badge={
								<UserStatusBadge
									lockedAt={u.authenticationUser?.lockedAt}
									lastActive={u.authenticationUser?.lastActive}
								/>
							}
							metrics={[
								{
									label: 'Role',
									value: (
										<StatusBadge
											active={
												u.authenticationUser?.role === AuthRoleEnum.Admin
											}
											activeLabel={u.authenticationUser?.role || 'No role'}
											inactiveLabel={u.authenticationUser?.role || 'No role'}
											activeTone="info"
											inactiveTone="neutral"
										/>
									),
								},
								{
									label: 'Status',
									value: userStatusLabel(
										u.authenticationUser?.lockedAt,
										u.authenticationUser?.lastActive
									),
								},
								{
									label: 'Created',
									value: u.createdAt ? formatDate(u.createdAt) : '—',
								},
							]}
							metricColumns={3}
							onClick={() =>
								setEditTarget({
									id: u.id,
									firstName: u.firstName,
									lastName: u.lastName,
									email: u.authenticationUser?.email || undefined,
								})
							}
						/>
					))}
				</div>
			) : (
				/* ── List/table view ── */
				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[700px]">
							<TableHeader>
								<TableRow className="border-line-1 hover:bg-transparent">
									<TableHead className="w-10">
										<Checkbox
											checked={allSelected}
											indeterminate={someSelected && !allSelected}
											onCheckedChange={toggleAll}
										/>
									</TableHead>
									<SortableTableHead
										sortKey="firstName"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Name
									</SortableTableHead>
									<SortableTableHead
										sortKey="email"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Email
									</SortableTableHead>
									<TableHead className="w-[100px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Role
									</TableHead>
									<TableHead className="w-[90px] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Status
									</TableHead>
									<SortableTableHead
										sortKey="createdAt"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[110px] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Created
									</SortableTableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{users.map((u) => (
									<TableRow
										key={u.id}
										data-state={selectedIds.has(u.id) ? 'selected' : undefined}
										className="cursor-pointer border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
										onClick={() =>
											setEditTarget({
												id: u.id,
												firstName: u.firstName,
												lastName: u.lastName,
												email: u.authenticationUser?.email || undefined,
											})
										}
									>
										<TableCell>
											<div onClick={(e) => e.stopPropagation()}>
												<Checkbox
													checked={selectedIds.has(u.id)}
													onCheckedChange={() => toggleOne(u.id)}
													disabled={isSelf(u)}
												/>
											</div>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-3">
												<InitialsAvatar
													name={`${u.firstName} ${u.lastName}`}
													size={32}
													shape="rounded"
												/>
												<span className="text-base font-medium text-text-1">
													{u.firstName} {u.lastName}
												</span>
											</div>
										</TableCell>
										<TableCell className="text-base text-text-2">
											{u.authenticationUser?.email || '—'}
										</TableCell>
										<TableCell>
											<StatusBadge
												active={
													u.authenticationUser?.role ===
													AuthRoleEnum.Admin
												}
												activeLabel={
													u.authenticationUser?.role || 'No role'
												}
												inactiveLabel={
													u.authenticationUser?.role || 'No role'
												}
												activeTone="info"
												inactiveTone="neutral"
											/>
										</TableCell>
										<TableCell>
											<UserStatusBadge
												lockedAt={u.authenticationUser?.lockedAt}
												lastActive={u.authenticationUser?.lastActive}
											/>
										</TableCell>
										<TableCell className="text-base text-text-3">
											{u.createdAt ? formatDate(u.createdAt) : '—'}
										</TableCell>
										<TableCell>
											<div className="flex justify-end">
												<DropdownMenu>
													<DropdownMenuTrigger
														onClick={(e) => e.stopPropagation()}
														aria-label="User actions"
														className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
													>
														<MoreHorizontal size={14} />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															onClick={(e) => {
																e.stopPropagation();
																setEditTarget({
																	id: u.id,
																	firstName: u.firstName,
																	lastName: u.lastName,
																	email:
																		u.authenticationUser
																			?.email || undefined,
																});
															}}
														>
															<Pencil size={14} /> Edit
														</DropdownMenuItem>
														<DropdownMenuItem
															variant="destructive"
															disabled={isSelf(u)}
															onClick={(e) => {
																e.stopPropagation();
																setDeleteTarget({
																	id: u.id,
																	name: `${u.firstName} ${u.lastName}`,
																});
															}}
														>
															<Trash2 size={14} />{' '}
															{isSelf(u)
																? "Can't delete yourself"
																: 'Delete'}
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</Card>
			)}

			<SelectionBar
				count={selectedIds.size}
				actions={[
					{ label: 'Delete', icon: Trash2, onClick: () => setBulkDeleteOpen(true) },
				]}
				onClear={() => setSelectedIds(new Set())}
			/>

			{/* ── Pagination ── */}
			<ListFooter list={list} />

			{/* ── Modals ── */}
			<InviteUserModal
				open={inviteOpen}
				onOpenChange={setInviteOpen}
				onSuccess={() => {
					reload(refetch);
					refetchStats();
				}}
			/>

			<EditUserModal
				open={!!editTarget}
				onOpenChange={(open) => {
					if (!open) setEditTarget(null);
				}}
				user={editTarget}
				onSuccess={() => {
					setEditTarget(null);
					reload(refetch);
				}}
			/>

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
				title="Delete user"
				description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
				onConfirm={handleDelete}
				loading={deleting}
			/>

			<ConfirmDialog
				open={bulkDeleteOpen}
				onOpenChange={setBulkDeleteOpen}
				title="Delete users"
				description={`Are you sure you want to delete ${selectedIds.size} user${selectedIds.size > 1 ? 's' : ''}? This action cannot be undone.`}
				onConfirm={handleBulkDelete}
				loading={bulkDeleting}
			/>
		</div>
	);
}
